import type { TowerType } from '../assets/modelFactories/TowerFactory';
import { TOWER_STATS } from '../entities/Tower';

export class Hud {
  private readonly livesValue = this.getElement('#lives-value');
  private readonly moneyValue = this.getElement('#money-value');
  private readonly waveValue = this.getElement('#wave-value');
  private readonly totalWavesValue = this.getElement('#total-waves-value');
  private readonly statusLine = this.getElement('#status-line');
  private readonly waveBanner = this.getElement('#wave-banner');
  private readonly waveTitle = this.getElement('#wave-title');
  private readonly waveSubtitle = this.getElement('#wave-subtitle');
  private readonly overlay = this.getElement('#overlay');
  private readonly overlayTitle = this.getElement('#overlay-title');
  private readonly overlayMessage = this.getElement('#overlay-message');
  private readonly overlayAction = this.getElement('#overlay-action') as HTMLButtonElement;
  private readonly towerCards = Array.from(document.querySelectorAll<HTMLElement>('.tower-card'));
  private readonly speedButton = this.getElement('#speed-button');

  private bannerTimer = 0;
  private onTowerSelectCallback?: (type: TowerType | null) => void;
  private onOverlayActionCallback?: () => void;
  private onSpeedToggleCallback?: (active: boolean) => void;

  constructor() {
    for (const card of this.towerCards) {
      card.addEventListener('click', () => {
        const type = card.dataset.type as TowerType;
        this.onTowerSelectCallback?.(type);
      });
    }

    this.overlayAction.addEventListener('click', () => {
      this.onOverlayActionCallback?.();
    });

    this.speedButton.addEventListener('click', () => {
      this.onSpeedToggleCallback?.(!this.speedButton.classList.contains('active'));
    });
  }

  setTotalWaves(total: number): void {
    this.totalWavesValue.textContent = String(total);
  }

  update(lives: number, money: number, wave: number, status: string, isLowLives: boolean): void {
    this.livesValue.textContent = String(lives);
    this.moneyValue.textContent = `$${money}`;
    this.waveValue.textContent = String(wave);
    this.statusLine.textContent = status;

    if (isLowLives) {
      this.statusLine.classList.add('danger');
    } else {
      this.statusLine.classList.remove('danger');
    }

    for (const card of this.towerCards) {
      const type = card.dataset.type as TowerType;
      const cost = TOWER_STATS[type].cost;
      if (money < cost) {
        card.classList.add('disabled');
      } else {
        card.classList.remove('disabled');
      }
    }
  }

  setSelectedTower(type: TowerType | null): void {
    for (const card of this.towerCards) {
      if (card.dataset.type === type) {
        card.classList.add('selected');
      } else {
        card.classList.remove('selected');
      }
    }
  }

  showWaveBanner(title: string, subtitle: string, duration = 2.5): void {
    this.waveTitle.textContent = title;
    this.waveSubtitle.textContent = subtitle;
    this.waveBanner.classList.remove('hidden');
    this.bannerTimer = duration;
  }

  hideWaveBanner(): void {
    this.waveBanner.classList.add('hidden');
  }

  updateBanner(delta: number): void {
    if (this.bannerTimer > 0) {
      this.bannerTimer -= delta;
      if (this.bannerTimer <= 0) {
        this.hideWaveBanner();
      }
    }
  }

  showOverlay(title: string, message: string, action: string): void {
    this.overlayTitle.textContent = title;
    this.overlayMessage.textContent = message;
    this.overlayAction.textContent = action;
    this.overlay.classList.remove('hidden');
  }

  hideOverlay(): void {
    this.overlay.classList.add('hidden');
  }

  isOverlayVisible(): boolean {
    return !this.overlay.classList.contains('hidden');
  }

  setSpeedActive(active: boolean): void {
    this.speedButton.classList.toggle('active', active);
  }

  onTowerSelect(callback: (type: TowerType | null) => void): void {
    this.onTowerSelectCallback = callback;
  }

  onOverlayAction(callback: () => void): void {
    this.onOverlayActionCallback = callback;
  }

  onSpeedToggle(callback: (active: boolean) => void): void {
    this.onSpeedToggleCallback = callback;
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
